import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "./ui/collapsible";
import { ImageCropDialog } from "./ImageCropDialog";
import { ChevronDown, ChevronUp, Upload, Image, RotateCcw, Trash2, Check, Info, FlaskConical } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { useTheme } from "@/providers/ThemeProvider";

export interface ImportedDayEntry {
  date: string; // YYYY-MM-DD
  value: number;
}

/** Color level info returned from extraction for displaying the legend */
export interface ColorLevel {
  r: number;
  g: number;
  b: number;
  level: number; // 0-4
  mappedValue: number;
  count: number;
}

interface ScreenshotImportSectionProps {
  metricType: "numeric" | "boolean";
  onImportData: (data: ImportedDayEntry[] | null) => void;
  importedData: ImportedDayEntry[] | null;
}

type ShadeMode = "shades" | "yesno";
type DayOrder = "mon-sun" | "sun-sat";

interface ExtractionResult {
  entries: ImportedDayEntry[];
  colorLevels: ColorLevel[];
  gridInfo: { cols: number; rows: number };
}

// ─── Utility functions ────────────────────────────────────────

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ─── Grid detection ───────────────────────────────────────────

/**
 * Project image onto one axis by computing per-row or per-column average brightness.
 * Gaps between squares tend to be a consistent background color, while square interiors vary.
 * We look for periodic "gap" stripes to find the grid pitch.
 */
function computeAxisProfile(imageData: ImageData, axis: "x" | "y"): number[] {
  const { width, height, data } = imageData;
  const len = axis === "x" ? width : height;
  const perp = axis === "x" ? height : width;
  const profile: number[] = new Array(len).fill(0);

  for (let i = 0; i < len; i++) {
    let sum = 0;
    // Sample every 2nd pixel for speed on large images
    const step = perp > 200 ? 2 : 1;
    let count = 0;
    for (let j = 0; j < perp; j += step) {
      const px = axis === "x" ? i : j;
      const py = axis === "x" ? j : i;
      const idx = (py * width + px) * 4;
      // Use luminance
      sum += data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
      count++;
    }
    profile[i] = sum / count;
  }
  return profile;
}

/**
 * Find the dominant period in a 1D profile using autocorrelation.
 * The grid pitch = cell_size + gap_size will show as the strongest repeating period.
 */
function findPeriod(profile: number[], minPeriod: number, maxPeriod: number): number {
  const n = profile.length;
  // Compute mean
  let mean = 0;
  for (let i = 0; i < n; i++) mean += profile[i];
  mean /= n;

  // Autocorrelation for each candidate period
  let bestPeriod = 0;
  let bestCorr = -Infinity;

  for (let p = minPeriod; p <= maxPeriod; p++) {
    let corr = 0;
    let count = 0;
    for (let i = 0; i < n - p; i++) {
      corr += (profile[i] - mean) * (profile[i + p] - mean);
      count++;
    }
    corr /= count;
    if (corr > bestCorr) {
      bestCorr = corr;
      bestPeriod = p;
    }
  }
  return bestPeriod;
}

/**
 * Once we know the period, find where cells start by looking for the gap pattern.
 * Gaps tend to be brighter/lighter (background color). We find local brightness peaks
 * spaced ~period apart, and the cell centers are between them.
 */
function findCellCenters(profile: number[], period: number): number[] {
  if (period <= 2) return [];
  const n = profile.length;

  // Smooth the profile to reduce noise
  const smoothed = new Array(n).fill(0);
  const kernel = Math.max(1, Math.floor(period * 0.15));
  for (let i = 0; i < n; i++) {
    let sum = 0; let cnt = 0;
    for (let k = -kernel; k <= kernel; k++) {
      if (i + k >= 0 && i + k < n) { sum += profile[i + k]; cnt++; }
    }
    smoothed[i] = sum / cnt;
  }

  // Find the offset that best aligns gaps with brightness peaks.
  // Try each offset within one period and score by how well peaks line up with gaps.
  let bestOffset = 0;
  let bestScore = -Infinity;

  for (let off = 0; off < period; off++) {
    let score = 0;
    for (let pos = off; pos < n; pos += period) {
      // The gap should be brighter (higher luminance) at this position
      const start = Math.max(0, pos - Math.floor(period * 0.1));
      const end = Math.min(n - 1, pos + Math.floor(period * 0.1));
      for (let i = start; i <= end; i++) score += smoothed[i];
    }
    if (score > bestScore) { bestScore = score; bestOffset = off; }
  }

  // Gap positions: bestOffset, bestOffset+period, ...
  // Cell centers are between consecutive gaps
  const centers: number[] = [];
  const halfPeriod = Math.floor(period / 2);

  // First center might be before the first gap
  const firstGap = bestOffset;
  if (firstGap >= halfPeriod) {
    centers.push(Math.round(firstGap - halfPeriod));
  }

  for (let gap = firstGap; gap + period < n; gap += period) {
    const center = Math.round(gap + halfPeriod);
    if (center >= 0 && center < n) {
      centers.push(center);
    }
  }

  return centers;
}

/**
 * Main grid detection: finds a grid of squares in the image.
 * Returns cell center coordinates for a grid of 7 rows x N columns.
 */
function detectGridCells(imageData: ImageData): { centers: { x: number; y: number }[][]; cols: number; rows: number } | null {
  const { width, height } = imageData;

  // The image should be mostly the grid (user cropped to it).
  // Grid = 7 rows, variable columns. Squares are somewhat equally sized.

  // Compute axis profiles
  const xProfile = computeAxisProfile(imageData, "x");
  const yProfile = computeAxisProfile(imageData, "y");

  // For Y axis: we know there are 7 rows, so period ≈ height / 7
  const expectedYPeriod = height / 7;
  const yPeriod = findPeriod(yProfile, Math.floor(expectedYPeriod * 0.6), Math.ceil(expectedYPeriod * 1.5));

  if (yPeriod <= 2) return null;

  // For X axis: period should be similar to Y (squares are square!)
  // But allow some tolerance for non-square cells or different gaps
  const xMinPeriod = Math.floor(yPeriod * 0.5);
  const xMaxPeriod = Math.ceil(yPeriod * 2.0);
  const xPeriod = findPeriod(xProfile, xMinPeriod, Math.min(xMaxPeriod, Math.floor(width / 2)));

  if (xPeriod <= 2) return null;

  // Find cell centers along each axis
  const xCenters = findCellCenters(xProfile, xPeriod);
  const yCenters = findCellCenters(yProfile, yPeriod);

  // Validate: we need exactly 7 rows (or close to it)
  // Allow some flexibility: 6-8 detected rows, but we'll only use 7
  if (yCenters.length < 5 || yCenters.length > 9) {
    // Fallback: force 7 evenly spaced rows
    const rowCenters: number[] = [];
    const cellH = height / 7;
    for (let i = 0; i < 7; i++) {
      rowCenters.push(Math.round(cellH * i + cellH / 2));
    }
    const colCenters = xCenters.length >= 2 ? xCenters : [];
    if (colCenters.length === 0) {
      // Force columns too
      const cellW = xPeriod > 2 ? xPeriod : yPeriod;
      const numCols = Math.max(1, Math.round(width / cellW));
      for (let i = 0; i < numCols; i++) {
        colCenters.push(Math.round(cellW * i + cellW / 2));
      }
    }
    return buildGrid(colCenters, rowCenters);
  }

  // Trim to 7 rows if we found more
  const finalYCenters = yCenters.length > 7 ? yCenters.slice(0, 7) : yCenters;
  // Pad to 7 if we found 5-6
  while (finalYCenters.length < 7 && finalYCenters.length >= 2) {
    const gap = finalYCenters[1] - finalYCenters[0];
    const next = finalYCenters[finalYCenters.length - 1] + gap;
    if (next < height) finalYCenters.push(next);
    else break;
  }

  if (xCenters.length < 1) return null;

  return buildGrid(xCenters, finalYCenters);
}

function buildGrid(xCenters: number[], yCenters: number[]): { centers: { x: number; y: number }[][]; cols: number; rows: number } {
  const cols = xCenters.length;
  const rows = yCenters.length;
  const centers: { x: number; y: number }[][] = [];

  for (let col = 0; col < cols; col++) {
    const column: { x: number; y: number }[] = [];
    for (let row = 0; row < rows; row++) {
      column.push({ x: xCenters[col], y: yCenters[row] });
    }
    centers.push(column);
  }

  return { centers, cols, rows };
}

// ─── Color sampling & classification ──────────────────────────

/**
 * Sample color at a cell center, averaging a small region for stability.
 */
function sampleColor(imageData: ImageData, cx: number, cy: number, radius: number = 2): { r: number; g: number; b: number } {
  const { width, height, data } = imageData;
  let rSum = 0, gSum = 0, bSum = 0, count = 0;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const x = Math.min(width - 1, Math.max(0, Math.round(cx + dx)));
      const y = Math.min(height - 1, Math.max(0, Math.round(cy + dy)));
      const idx = (y * width + x) * 4;
      rSum += data[idx]; gSum += data[idx + 1]; bSum += data[idx + 2];
      count++;
    }
  }
  return { r: Math.round(rSum / count), g: Math.round(gSum / count), b: Math.round(bSum / count) };
}

/**
 * Classify sampled colors into discrete levels (0-4) using k-means-like clustering
 * on luminance. Returns the level mapping and representative colors for each level.
 */
function classifyColors(
  colors: { r: number; g: number; b: number }[],
  numLevels: number
): { assignments: number[]; centroids: { r: number; g: number; b: number; lum: number }[] } {
  // Compute luminance for each color
  const lums = colors.map(c => c.r * 0.299 + c.g * 0.587 + c.b * 0.114);
  const minLum = Math.min(...lums);
  const maxLum = Math.max(...lums);
  const lumRange = maxLum - minLum;

  if (lumRange < 3) {
    // All same color - assign everything to level 0
    return {
      assignments: colors.map(() => 0),
      centroids: [{ r: colors[0].r, g: colors[0].g, b: colors[0].b, lum: lums[0] }],
    };
  }

  // Initialize centroids evenly across luminance range
  // Level 0 = brightest (empty), Level N = darkest (most active)
  const centroids: number[] = [];
  for (let i = 0; i < numLevels; i++) {
    centroids.push(maxLum - (i / (numLevels - 1)) * lumRange);
  }

  // K-means iterations
  let assignments = new Array(colors.length).fill(0);
  for (let iter = 0; iter < 10; iter++) {
    // Assign each color to nearest centroid
    const newAssignments: number[] = [];
    for (let i = 0; i < lums.length; i++) {
      let bestLevel = 0;
      let bestDist = Infinity;
      for (let l = 0; l < numLevels; l++) {
        const dist = Math.abs(lums[i] - centroids[l]);
        if (dist < bestDist) { bestDist = dist; bestLevel = l; }
      }
      newAssignments.push(bestLevel);
    }
    assignments = newAssignments;

    // Update centroids
    for (let l = 0; l < numLevels; l++) {
      let sum = 0; let cnt = 0;
      for (let i = 0; i < lums.length; i++) {
        if (assignments[i] === l) { sum += lums[i]; cnt++; }
      }
      if (cnt > 0) centroids[l] = sum / cnt;
    }
  }

  // Build representative color for each level (average RGB of assigned cells)
  const levelColors: { r: number; g: number; b: number; lum: number }[] = [];
  for (let l = 0; l < numLevels; l++) {
    let rS = 0, gS = 0, bS = 0, cnt = 0;
    for (let i = 0; i < colors.length; i++) {
      if (assignments[i] === l) { rS += colors[i].r; gS += colors[i].g; bS += colors[i].b; cnt++; }
    }
    if (cnt > 0) {
      levelColors.push({ r: Math.round(rS / cnt), g: Math.round(gS / cnt), b: Math.round(bS / cnt), lum: centroids[l] });
    } else {
      levelColors.push({ r: 200, g: 200, b: 200, lum: centroids[l] });
    }
  }

  return { assignments, centroids: levelColors };
}

// ─── Main extraction ──────────────────────────────────────────

async function extractGridData(
  imageBlob: Blob,
  shadeMode: ShadeMode,
  dayOrder: DayOrder,
  rangeMin: number,
  rangeMax: number,
  startDate: Date,
  endDate: Date
): Promise<ExtractionResult> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Cannot get canvas context")); return; }
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, img.width, img.height);

        // Step 1: Detect grid
        const grid = detectGridCells(imageData);
        if (!grid || grid.cols === 0 || grid.rows === 0) {
          reject(new Error("Could not detect grid"));
          return;
        }

        // Step 2: Sample colors at each cell center
        const allColors: { r: number; g: number; b: number }[] = [];
        const colorGrid: { r: number; g: number; b: number }[][] = [];
        // Determine sample radius based on grid density
        const sampleRadius = Math.max(1, Math.min(5, Math.floor(img.width / grid.cols / 6)));

        for (let col = 0; col < grid.cols; col++) {
          const column: { r: number; g: number; b: number }[] = [];
          for (let row = 0; row < grid.rows; row++) {
            const { x, y } = grid.centers[col][row];
            const color = sampleColor(imageData, x, y, sampleRadius);
            column.push(color);
            allColors.push(color);
          }
          colorGrid.push(column);
        }

        // Step 3: Classify colors into levels
        const numLevels = shadeMode === "yesno" ? 2 : 5;
        const { assignments, centroids } = classifyColors(allColors, numLevels);

        // Step 4: Build color level legend
        const colorLevels: ColorLevel[] = [];
        for (let l = 0; l < centroids.length; l++) {
          const count = assignments.filter(a => a === l).length;
          let mappedValue: number;
          if (shadeMode === "yesno") {
            mappedValue = l === 0 ? 0 : 1;
          } else {
            mappedValue = Math.round(rangeMin + (l / (numLevels - 1)) * (rangeMax - rangeMin));
          }
          colorLevels.push({
            r: centroids[l].r,
            g: centroids[l].g,
            b: centroids[l].b,
            level: l,
            mappedValue,
            count,
          });
        }

        // Step 5: Map to date entries
        const entries: ImportedDayEntry[] = [];
        const daysOfWeek = dayOrder === "mon-sun"
          ? [1, 2, 3, 4, 5, 6, 0]
          : [0, 1, 2, 3, 4, 5, 6];

        const startDayOfWeek = startDate.getDay();
        const startRowIndex = daysOfWeek.indexOf(startDayOfWeek);

        let colorIdx = 0;
        for (let col = 0; col < grid.cols; col++) {
          for (let row = 0; row < Math.min(grid.rows, 7); row++) {
            const dayOffset = col * 7 + (row - startRowIndex);
            colorIdx = col * grid.rows + row;

            if (dayOffset < 0) continue;
            const date = new Date(startDate);
            date.setDate(date.getDate() + dayOffset);
            if (date > endDate) continue;

            const level = assignments[colorIdx];
            let value: number;
            if (shadeMode === "yesno") {
              value = level === 0 ? 0 : 1;
            } else {
              value = Math.round(rangeMin + (level / (numLevels - 1)) * (rangeMax - rangeMin));
            }

            entries.push({ date: formatDate(date), value });
          }
        }

        resolve({
          entries,
          colorLevels,
          gridInfo: { cols: grid.cols, rows: grid.rows },
        });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(imageBlob);
  });
}

/** Build a mini preview grid from the imported data */
function MiniPreviewGrid({
  data,
  metricType,
  rangeMin,
  rangeMax,
}: {
  data: ImportedDayEntry[];
  metricType: "numeric" | "boolean";
  rangeMin: number;
  rangeMax: number;
}) {
  const { theme } = useTheme();
  if (data.length === 0) return null;

  // Sort by date
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
  const firstDate = new Date(sorted[0].date + "T00:00:00");
  const lastDate = new Date(sorted[sorted.length - 1].date + "T00:00:00");

  // Build a map for quick lookup
  const dateMap = new Map<string, number>();
  for (const entry of sorted) {
    dateMap.set(entry.date, entry.value);
  }

  // Calculate weeks and build grid (columns = weeks, rows = days)
  // Start from the Sunday/Monday before the first date
  const startDay = new Date(firstDate);
  startDay.setDate(startDay.getDate() - startDay.getDay()); // Go to Sunday

  const weeks: (ImportedDayEntry | null)[][] = [];
  const current = new Date(startDay);
  while (current <= lastDate || weeks.length === 0) {
    const week: (ImportedDayEntry | null)[] = [];
    for (let d = 0; d < 7; d++) {
      const dateStr = formatDate(current);
      const val = dateMap.get(dateStr);
      if (val !== undefined && current >= firstDate && current <= lastDate) {
        week.push({ date: dateStr, value: val });
      } else {
        week.push(null);
      }
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
  }

  const getColor = (value: number | null): string => {
    if (value === null) return "transparent";
    const emptyColor = theme === "light" ? "hsl(0, 0%, 88%)" : "hsl(0, 0%, 22%)";

    if (metricType === "boolean") {
      return value > 0 ? "hsl(142, 70%, 45%)" : emptyColor;
    }

    // Numeric: map to 5-level green
    if (value <= 0) return emptyColor;
    const range = rangeMax - rangeMin;
    if (range <= 0) return emptyColor;
    const ratio = Math.min(1, Math.max(0, (value - rangeMin) / range));
    const level = Math.round(ratio * 4);
    const greens = [
      emptyColor,
      "hsl(142, 40%, 70%)",
      "hsl(142, 50%, 55%)",
      "hsl(142, 60%, 42%)",
      "hsl(142, 70%, 32%)",
    ];
    return greens[level];
  };

  return (
    <div className="flex gap-[2px] overflow-x-auto py-1">
      {weeks.map((week, wi) => (
        <div key={wi} className="flex flex-col gap-[2px]">
          {week.map((entry, di) => (
            <div
              key={di}
              className="rounded-sm"
              style={{
                width: weeks.length > 26 ? 6 : weeks.length > 13 ? 8 : 10,
                height: weeks.length > 26 ? 6 : weeks.length > 13 ? 8 : 10,
                backgroundColor: getColor(entry?.value ?? null),
              }}
              title={entry ? `${entry.date}: ${entry.value}` : ""}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

const ScreenshotImportSection = ({ metricType, onImportData, importedData }: ScreenshotImportSectionProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  const [shadeMode, setShadeMode] = useState<ShadeMode>(metricType === "boolean" ? "yesno" : "shades");
  const [dayOrder, setDayOrder] = useState<DayOrder>("mon-sun");
  const [rangeMin, setRangeMin] = useState(0);
  const [rangeMax, setRangeMax] = useState(100);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [colorLevels, setColorLevels] = useState<ColorLevel[]>([]);
  const [gridInfo, setGridInfo] = useState<{ cols: number; rows: number } | null>(null);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
    // Reset for re-selection
    e.target.value = "";
  }, []);

  const handleCropComplete = useCallback((blob: Blob) => {
    setCroppedBlob(blob);
    setCroppedPreview(URL.createObjectURL(blob));
    setShowCropDialog(false);
    setError(null);
    setColorLevels([]);
    setGridInfo(null);
    // Clear previous imported data when new image is cropped
    onImportData(null);
  }, [onImportData]);

  const handleExtract = useCallback(async () => {
    if (!croppedBlob) {
      setError(t("components.custom_metrics_dialog.screenshot_import.no_image"));
      return;
    }
    if (!startDate || !endDate) {
      setError(t("components.custom_metrics_dialog.screenshot_import.no_dates"));
      return;
    }
    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");
    if (end <= start) {
      setError(t("components.custom_metrics_dialog.screenshot_import.invalid_range"));
      return;
    }
    if (shadeMode === "shades" && rangeMax <= rangeMin) {
      setError(t("components.custom_metrics_dialog.screenshot_import.invalid_range"));
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const result = await extractGridData(
        croppedBlob,
        shadeMode,
        dayOrder,
        shadeMode === "yesno" ? 0 : rangeMin,
        shadeMode === "yesno" ? 1 : rangeMax,
        start,
        end
      );

      if (result.entries.length === 0) {
        setError(t("components.custom_metrics_dialog.screenshot_import.extraction_failed"));
        onImportData(null);
        setColorLevels([]);
        setGridInfo(null);
      } else {
        onImportData(result.entries);
        setColorLevels(result.colorLevels);
        setGridInfo(result.gridInfo);
      }
    } catch {
      setError(t("components.custom_metrics_dialog.screenshot_import.extraction_failed"));
      onImportData(null);
      setColorLevels([]);
      setGridInfo(null);
    } finally {
      setIsAnalyzing(false);
    }
  }, [croppedBlob, startDate, endDate, shadeMode, dayOrder, rangeMin, rangeMax, onImportData, t]);

  const handleDiscard = useCallback(() => {
    onImportData(null);
    setCroppedBlob(null);
    setCroppedPreview(null);
    setImageSrc(null);
    setError(null);
    setColorLevels([]);
    setGridInfo(null);
  }, [onImportData]);

  const prefix = "components.custom_metrics_dialog.screenshot_import";

  return (
    <>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between gap-2 w-full p-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer text-left mt-2"
          >
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t(`${prefix}.title`)}</span>
            </div>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-3 p-3 border border-dashed rounded-lg mt-1">
            {/* Experimental badge */}
            <div className="flex items-center gap-1.5 text-[10px] font-medium text-amber-500 bg-amber-500/10 rounded-md px-2 py-1 w-fit">
              <FlaskConical className="w-3 h-3" />
              {t(`${prefix}.experimental`)}
            </div>

            {/* Instructions */}
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Info className="w-3 h-3" />
                {t(`${prefix}.instructions_title`)}
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5 pl-4 list-disc">
                <li>{t(`${prefix}.instruction_grid`)}</li>
                <li>{t(`${prefix}.instruction_shades`)}</li>
                <li>{t(`${prefix}.instruction_range`)}</li>
                <li>{t(`${prefix}.instruction_crop`)}</li>
                <li>{t(`${prefix}.instruction_dates`)}</li>
              </ul>
            </div>

            {/* Image upload */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
              {croppedPreview ? (
                <div className="space-y-2">
                  <div className="relative rounded-md overflow-hidden border bg-muted/20">
                    <img
                      src={croppedPreview}
                      alt={t(`${prefix}.crop_instruction`)}
                      className="w-full max-h-32 object-contain"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {t(`${prefix}.change_image`)}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {t(`${prefix}.upload_image`)}
                </Button>
              )}
            </div>

            {/* Settings row */}
            {croppedBlob && (
              <div className="space-y-3">
                {/* Grid type */}
                <div className="space-y-1">
                  <Label className="text-xs">{t(`${prefix}.shade_mode_label`)}</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={shadeMode === "shades" ? "default" : "outline"}
                      size="sm"
                      className="flex-1 text-xs h-7"
                      onClick={() => setShadeMode("shades")}
                    >
                      {t(`${prefix}.shade_mode_shades`)}
                    </Button>
                    <Button
                      type="button"
                      variant={shadeMode === "yesno" ? "default" : "outline"}
                      size="sm"
                      className="flex-1 text-xs h-7"
                      onClick={() => setShadeMode("yesno")}
                    >
                      {t(`${prefix}.shade_mode_yesno`)}
                    </Button>
                  </div>
                </div>

                {/* Range mapping for shades */}
                {shadeMode === "shades" && (
                  <div className="space-y-1">
                    <Label className="text-xs">{t(`${prefix}.range_desc`)}</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-[10px] text-muted-foreground">{t(`${prefix}.range_min_label`)}</Label>
                        <Input
                          type="number"
                          value={rangeMin}
                          onChange={(e) => setRangeMin(Number(e.target.value))}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-[10px] text-muted-foreground">{t(`${prefix}.range_max_label`)}</Label>
                        <Input
                          type="number"
                          value={rangeMax}
                          onChange={(e) => setRangeMax(Number(e.target.value))}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Day order */}
                <div className="space-y-1">
                  <Label className="text-xs">{t(`${prefix}.day_order_label`)}</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={dayOrder === "mon-sun" ? "default" : "outline"}
                      size="sm"
                      className="flex-1 text-xs h-7"
                      onClick={() => setDayOrder("mon-sun")}
                    >
                      {t(`${prefix}.day_order_mon_sun`)}
                    </Button>
                    <Button
                      type="button"
                      variant={dayOrder === "sun-sat" ? "default" : "outline"}
                      size="sm"
                      className="flex-1 text-xs h-7"
                      onClick={() => setDayOrder("sun-sat")}
                    >
                      {t(`${prefix}.day_order_sun_sat`)}
                    </Button>
                  </div>
                </div>

                {/* Date range */}
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">{t(`${prefix}.start_date_label`)}</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">{t(`${prefix}.end_date_label`)}</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-7 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Extract button */}
                <Button
                  type="button"
                  size="sm"
                  className="w-full text-xs"
                  onClick={handleExtract}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <>
                      <MiniGridLoader className="w-3 h-3 mr-1" />
                      {t(`${prefix}.analyzing`)}
                    </>
                  ) : (
                    t(`${prefix}.extract_button`)
                  )}
                </Button>

                {/* Error */}
                {error && (
                  <p className="text-xs text-destructive">{error}</p>
                )}

                {/* Preview */}
                {importedData && importedData.length > 0 && (
                  <div className="space-y-2 p-2 border rounded-md bg-muted/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Check className="w-3 h-3 text-green-500" />
                        <span className="text-xs font-medium">{t(`${prefix}.preview_title`)}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {t(`${prefix}.entries_found`, { count: importedData.length })}
                      </span>
                    </div>

                    {/* Color legend */}
                    {colorLevels.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {t(`${prefix}.color_legend`)}
                          </span>
                          {gridInfo && (
                            <span className="text-[10px] text-muted-foreground">
                              ({t(`${prefix}.grid_size`, { cols: gridInfo.cols, rows: gridInfo.rows })})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {colorLevels.map((cl, idx) => (
                            <div key={idx} className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-[10px]">
                              <div
                                className="w-3 h-3 rounded-sm border border-border"
                                style={{ backgroundColor: `rgb(${cl.r}, ${cl.g}, ${cl.b})` }}
                              />
                              <span className="font-mono">{cl.mappedValue}</span>
                              <span className="text-muted-foreground">({cl.count})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <MiniPreviewGrid
                      data={importedData}
                      metricType={metricType}
                      rangeMin={rangeMin}
                      rangeMax={rangeMax}
                    />

                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs h-7"
                        onClick={handleExtract}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        {t(`${prefix}.retry_button`)}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs h-7 text-destructive hover:text-destructive"
                        onClick={handleDiscard}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        {t(`${prefix}.discard_button`)}
                      </Button>
                    </div>

                    <p className="text-[10px] text-muted-foreground text-center">
                      {t(`${prefix}.keep_data_label`)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Crop dialog */}
      {imageSrc && (
        <ImageCropDialog
          open={showCropDialog}
          onOpenChange={setShowCropDialog}
          imageSrc={imageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={false}
          maxWidth={4096}
          maxHeight={4096}
          quality={1}
        />
      )}
    </>
  );
};

export default ScreenshotImportSection;
